//pages/Contact.tsx
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Layout from "@/components/Layout";
// SEO: Import SEO component for Contact + FAQ structured data.
import SEO from '@/components/SEO';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Send } from "lucide-react";

const SUBJECT_OPTIONS = [
  'Account & Login Issues',
  'Billing & Payments',
  'Tokens & Refunds',
  'Analysis Insights & Metrics',
  'Technical Problem (Bug/Error)',
  'Website Feedback',
  'Other / General Question'
] as const;

const contactFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  subject: z.enum(SUBJECT_OPTIONS),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export default function Contact() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      subject: undefined as any, // force explicit selection
      message: "",
    },
  });

  // Prefill name/email from authenticated profile when available
  useEffect(() => {
    if (!user) return;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    if (fullName) form.setValue("name", fullName);
    if (user.email) form.setValue("email", user.email);
  }, [user]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: data.name,
        email: data.email,
        subject: data.subject,
        message: data.message,
      };
      const res = await fetch(`${API_BASE_URL}/api/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = json?.detail || json?.message || res.statusText;
        throw new Error(msg || 'Failed to send message');
      }

      toast({
        title: "Message Sent!",
        description: json?.ticket_id
          ? `Your ticket ID: ${json.ticket_id}. Check your email for confirmation.`
          : "Thank you for contacting us. We'll get back to you soon.",
      });
      form.reset({
        name: form.getValues("name"),
        email: form.getValues("email"),
        subject: undefined as any,
        message: "",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: String((error as any)?.message || "Failed to send message. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: "How reliable are TradeSagePro analysis outputs?",
      answer:
        "Our AI-generated analysis insights report a Historical Concordance Rate based on backtesting and retrospective evaluation. For reference, prior models reported a 94.2% backtested reliability score in specific datasets and timeframes; this is a Model Validation Metric, not a guarantee of future outcomes. All trading involves risk and past performance is not indicative of future results.",
    },
    {
      question: "How long does it take to produce an analysis?",
      answer:
        "Typical processing ranges 2–5 minutes depending on market conditions and the complexity of the data-driven analysis required for the selected asset pair.",
    },
    {
      question: "Can I request insights for any crypto pair?",
      answer:
        "TradeSagePro can analyze most major crypto asset pairs. Our models leverage diverse market data to provide research reports and market data points for widely tracked and emerging cryptocurrencies, subject to data availability.",
    },
    {
      question: "What happens if I run out of tokens?",
      answer:
        "You can purchase additional tokens at any time through our pricing page. Packages are designed for different analysis volumes. Token usage aligns with analysis generation, not advisory outcomes.",
    },
    {
      question: "Do you offer refunds?",
      answer:
        "We offer a 30-day money-back guarantee on paid plans as described in our Terms of Service. Refund eligibility is evaluated per policy and does not relate to market performance or trading results.",
    },
  ];

  // FAQPage JSON-LD from visible FAQs to enable rich FAQ snippets.
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map(f => ({
      '@type': 'Question',
      'name': f.question,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.answer }
    }))
  };

  return (
    <Layout>
      {/* SEO TAGS: Contact page uses FAQPage structured data for enhanced SERP rich results. */}
      <SEO
        title="Contact TradeSagePro Support | AI Market Research Assistance"
        description="Get help with AI market research, analysis insights, tokens, billing and account issues. Reach TradeSagePro support or browse common FAQs before submitting a ticket."
        canonical="https://tradesagepro.com/contact"
        type="faq"
        jsonLd={faqSchema}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[hsl(226,26%,7%)] text-[hsl(210,9%,91%)] p-6">
        <div className="max-w-4xl mx-auto space-y-10">
          {/* Header */}
          <div className="bg-[hsl(226,26%,7%)] p-6 border-b border-gray-700 mb-6">
            <h1 className="text-3xl font-bold text-white mb-2">Contact Us</h1>
            <p className="text-[hsl(210,9%,91%)]">
              Have questions? Reach out to our team below.
            </p>
          </div>

          {/* Contact Form (Public - Auth not required) */}
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Send a Message</h2>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {["name", "email", "subject", "message"].map((fieldName) => (
                    <FormField
                      key={fieldName}
                      control={form.control}
                      name={fieldName as keyof ContactFormData}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-[hsl(210,9%,91%)] capitalize">
                            {fieldName}
                          </FormLabel>
                          <FormControl>
                            {fieldName === "message" ? (
                              <Textarea
                                placeholder="Your message..."
                                className="bg-[hsl(226,26%,7%)] border-gray-600 text-white focus:border-[hsl(193,100%,50%)] min-h-[120px]"
                                {...field}
                              />
                            ) : fieldName === 'subject' ? (
                              <select
                                className="w-full rounded-md bg-[hsl(226,26%,7%)] border border-gray-600 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(193,100%,50%)] focus:border-[hsl(193,100%,50%)]"
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value)}
                              >
                                <option value="" disabled>-- Select a category --</option>
                                {SUBJECT_OPTIONS.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : (
                              <Input
                                type={fieldName === "email" ? "email" : "text"}
                                placeholder={`Enter your ${fieldName}`}
                                className="bg-[hsl(226,26%,7%)] border-gray-600 text-white focus:border-[hsl(193,100%,50%)]"
                                {...field}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}

                  <Button
                    type="submit"
                    className="w-full py-3 bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] hover:scale-105 transition-all duration-300"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* FAQ Section */}
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700">
            <CardContent className="p-6">
              <h2 className="text-2xl font-bold text-white text-center mb-10">
                Frequently Asked Questions
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {faqs.map((faq, idx) => (
                  <div key={idx}>
                    <h3 className="text-lg font-semibold text-white mb-1">{faq.question}</h3>
                    <p className="text-[hsl(210,9%,91%)]">{faq.answer}</p>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <p className="text-[hsl(210,9%,91%)] mb-4">Still need help?</p>
                <Button
                  variant="outline"
                  onClick={() =>
                    document.querySelector("form")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="border-[hsl(193,100%,50%)] text-[hsl(193,100%,50%)] hover:text-white hover:bg-[hsl(193,100%,50%,0.1)]"
                >
                  Contact Support
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
    </Layout>
  );
}